import Fuse from 'fuse.js'
import {
  buildLocalRegistryMiniSearch,
  searchLocalRegistryMiniSearch,
} from './buildLocalRegistryMiniSearch'
import { foldText, foldingGetFn } from './textNormalize'

// Manually curated external / help-page links surfaced through the local
// search feed. Each URL can be bound to many search terms — Fuse weights
// the `terms` array twice as heavily as the label so a query of "printable"
// reliably maps to "Plickers Cards" even though "printable" is not in the label.
//
// To add a new entry, append to EXTERNAL_LINKS — no other file needs to
// change. Selections route through window.open(url, '_blank', 'noopener,noreferrer').
//
// Add `prefill: true` to an entry to surface it in the dropdown empty-state
// (focused, no query). None are flagged today — external links only appear
// while typing — but the empty-state builder gathers prefill-flagged entries
// across all registries, so this is ready for future use.

export const EXTERNAL_LINKS = [
  {
    id: 'ext-cards',
    label: 'Plickers Cards',
    url: 'https://help.plickers.com/hc/en-us/articles/360008948034',
    icon: 'info-circle',
    terms: ['cards', 'paper cards', 'print cards', 'card sheets', 'printable'],
  },
  {
    id: 'ext-support',
    label: 'Support / Help Center',
    url: 'https://help.plickers.com/',
    icon: 'info-circle',
    terms: ['support', 'help', 'contact', 'docs', 'documentation', 'faq', 'manual'],
  },
  {
    id: 'ext-privacy',
    label: 'Privacy Policy',
    url: 'https://www.plickers.com/privacy',
    icon: 'info-circle',
    terms: ['privacy', 'policy', 'gdpr', 'data'],
  },
  {
    id: 'ext-terms',
    label: 'Terms of Service',
    url: 'https://www.plickers.com/terms',
    icon: 'info-circle',
    terms: ['terms', 'tos', 'legal'],
  },
  {
    id: 'ext-ios-app',
    label: 'Get iOS App',
    url: 'https://itunes.apple.com/us/app/plickers/id701184049?mt=8',
    icon: 'external-link',
    terms: ['ios', 'iphone', 'ipad', 'apple', 'app store', 'download', 'mobile', 'app'],
  },
  {
    id: 'ext-android-app',
    label: 'Get Android App',
    url: 'https://play.google.com/store/apps/details?id=com.plickers.client.android',
    icon: 'external-link',
    terms: ['android', 'google play', 'download', 'mobile', 'app'],
  },
  // Help-center articles — surfaced with the info-circle icon.
  {
    id: 'ext-support-new-class',
    label: 'How to add a new Class',
    url: 'https://help.plickers.com/hc/en-us/articles/4402190537243-How-to-add-a-new-Class',
    icon: 'info-circle',
    terms: ['class', 'new'],
  },
  {
    id: 'ext-support-class-overview',
    label: 'Class homepage overview',
    url: 'https://help.plickers.com/hc/en-us/articles/360008949014-Class-Homepage-Overview',
    icon: 'info-circle',
    terms: ['class', 'student', 'students'],
  },
  {
    id: 'ext-support-set',
    label: 'What is a Set?',
    url: 'https://help.plickers.com/hc/en-us/articles/4403868347035-What-is-a-Set',
    icon: 'info-circle',
    terms: ['set', 'new'],
  },
  {
    id: 'ext-support-create-set',
    label: 'How to create a Set',
    url: 'https://help.plickers.com/hc/en-us/articles/4403869601691-How-to-create-a-new-Set-and-add-questions-to-it',
    icon: 'info-circle',
    terms: ['set', 'new'],
  },
  {
    id: 'ext-support-pack',
    label: 'What is a Pack?',
    url: 'https://help.plickers.com/hc/en-us/articles/360025736953-What-is-a-Pack',
    icon: 'info-circle',
    terms: ['pack', 'new'],
  },
  {
    id: 'ext-support-create-pack',
    label: 'How to create a Pack',
    url: 'https://help.plickers.com/hc/en-us/articles/4404314648091-How-to-create-a-Pack',
    icon: 'info-circle',
    terms: ['pack', 'new'],
  },
  {
    id: 'ext-support-share-content',
    label: 'Sharing content',
    url: 'https://help.plickers.com/hc/en-us/articles/360025736913-Sharing-content-Overview',
    icon: 'info-circle',
    terms: ['pack', 'share', 'sharing'],
  },
  {
    id: 'ext-support-collaborate',
    label: 'Collaborate on content',
    url: 'https://help.plickers.com/hc/en-us/articles/4407112942107-How-to-privately-share-and-collaborate-on-content-by-using-Packs',
    icon: 'info-circle',
    terms: ['pack', 'share', 'sharing'],
  },
  {
    id: 'ext-support-share-public',
    label: 'Share content publicly',
    url: 'https://help.plickers.com/hc/en-us/articles/360044728754-How-to-share-content-by-publishing-a-Pack',
    icon: 'info-circle',
    terms: ['pack', 'share', 'sharing'],
  },
  {
    id: 'ext-support-reports',
    label: 'What are Reports?',
    url: 'https://help.plickers.com/hc/en-us/articles/360008949114-What-are-Reports',
    icon: 'info-circle',
    terms: ['report', 'results', 'student', 'reports'],
  },
  {
    id: 'ext-support-access-reports',
    label: 'How to access Reports',
    url: 'https://help.plickers.com/hc/en-us/articles/4404709113243-How-to-access-Reports-to-view-student-results',
    icon: 'info-circle',
    terms: ['report', 'results', 'student', 'reports'],
  },
  {
    id: 'ext-support-scoresheet',
    label: 'What is the Scoresheet?',
    url: 'https://help.plickers.com/hc/en-us/articles/360008946014-What-is-the-Scoresheet',
    icon: 'info-circle',
    terms: ['scoresheet', 'results', 'student'],
  },
  {
    id: 'ext-support-access-scoresheet',
    label: 'How to access the Scoresheet',
    url: 'https://help.plickers.com/hc/en-us/articles/4404782713371-How-to-access-the-Scoresheet-to-view-student-results',
    icon: 'info-circle',
    terms: ['scoresheet', 'results', 'student'],
  },
  {
    id: 'ext-support-customize-scoresheet',
    label: 'How to customize your Scoresheet',
    url: 'https://help.plickers.com/hc/en-us/articles/4404774704283-How-to-customize-your-Scoresheet',
    icon: 'info-circle',
    terms: ['scoresheet', 'results', 'student'],
  },
  {
    id: 'ext-support-change-password',
    label: 'How to change your password',
    url: 'https://help.plickers.com/hc/en-us/articles/4419249609627-How-to-change-your-password',
    icon: 'info-circle',
    terms: ['password', 'account', 'security', 'change password'],
  },
  {
    id: 'ext-support-change-email',
    label: 'How to change your email address',
    url: 'https://help.plickers.com/hc/en-us/articles/4415750590363-How-to-change-the-email-address-linked-to-your-Plickers-account',
    icon: 'info-circle',
    terms: ['email', 'account', 'settings', 'update email'],
  },
  {
    id: 'ext-support-start',
    label: 'Getting Started',
    url: 'https://help.plickers.com/hc/en-us/categories/1260801472210-Getting-Started',
    icon: 'info-circle',
    terms: ['support', 'help', 'contact', 'docs', 'documentation', 'faq', 'manual', 'start', 'play'],
  },
  {
    id: 'ext-support-elearning',
    label: 'Use Plickers remotely: E-Learning',
    url: 'https://help.plickers.com/hc/en-us/articles/1260804066689-Setting-up-E-Learning',
    icon: 'info-circle',
    terms: ['remote', 'learning', 'e-learning'],
  },
  {
    id: 'ext-support-pro',
    label: 'What is Plickers Pro?',
    url: 'https://help.plickers.com/hc/en-us/articles/360042744134-What-is-Plickers-Pro',
    icon: 'info-circle',
    terms: ['pro', 'upgrade', 'billing', 'subscription', 'plan', 'premium', 'paid'],
  },
  {
    id: 'ext-support-plickers-pro-schools',
    label: 'What is Plickers Pro for Schools?',
    url: 'https://help.plickers.com/hc/en-us/articles/4406122282651-What-is-Plickers-Pro-for-Schools-group-order',
    icon: 'info-circle',
    terms: ['pro', 'upgrade', 'billing', 'subscription', 'plan', 'premium', 'paid', 'group', 'order', 'schools'],
  },
]

// Exported so the internal search-tuning sandbox can display/reset the defaults.
export const EXTERNAL_LINKS_FUSE_OPTIONS = {
  threshold: 0.2,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
  includeMatches: true,
  keys: [
    { name: 'terms', weight: 2 },
    { name: 'label', weight: 1 },
  ],
}

/**
 * Project a raw external-link entry into the Algolia-shaped hit consumed by the
 * dropdown. Used for the empty-state prefill list where no Fuse query is run.
 */
export function toExternalLinkHit(entry) {
  return {
    ...entry,
    objectID: entry.id,
    __external: true,
    __localOrigin: true,
  }
}

let cachedFuse = null

/**
 * Build (and cache) the Fuse external-links index. The registry is static
 * for the lifetime of the page so we only build once. The cache is keyed on
 * EXTERNAL_LINKS.length so adding entries automatically invalidates it.
 */
export function buildExternalLinksFuseIndex() {
  if (cachedFuse && cachedFuse.__entryCount === EXTERNAL_LINKS.length) return cachedFuse
  // `getFn` folds indexed values for accent-insensitive matching; injected here
  // to keep the exported options JSON-clean for the search-tuning sandbox.
  cachedFuse = new Fuse(EXTERNAL_LINKS, { ...EXTERNAL_LINKS_FUSE_OPTIONS, getFn: foldingGetFn })
  cachedFuse.__entryCount = EXTERNAL_LINKS.length
  return cachedFuse
}

/**
 * Run a query against the external-links Fuse index and return Algolia-shaped
 * flat hits, each carrying __external: true, __localOrigin: true, plus the
 * registry fields (label, url, icon, terms). The Fuse score and per-key
 * match metadata are passed through as __fuseScore and __fuseMatches for
 * devtools / searchReporter inspection.
 */
export function searchExternalLinks(fuse, query, limit = 6) {
  if (!query || !fuse) return []
  return fuse.search(foldText(query), { limit }).map(({ item, score, matches }) => ({
    ...item,
    objectID: item.id,
    __external: true,
    __localOrigin: true,
    __fuseScore: score,
    __fuseMatches: matches,
  }))
}

let cachedMiniSearch = null

/**
 * Build (and cache) the MiniSearch external-links index — the BM25 counterpart
 * of buildExternalLinksFuseIndex. Cache keyed on EXTERNAL_LINKS.length.
 */
export function buildExternalLinksMiniSearchIndex() {
  if (cachedMiniSearch && cachedMiniSearch.__entryCount === EXTERNAL_LINKS.length) return cachedMiniSearch
  cachedMiniSearch = buildLocalRegistryMiniSearch(EXTERNAL_LINKS)
  cachedMiniSearch.__entryCount = EXTERNAL_LINKS.length
  return cachedMiniSearch
}

/**
 * Run a query against the MiniSearch external-links index and return
 * Algolia-shaped hits mirroring searchExternalLinks, tagged with
 * __localEngine: 'minisearch' and __minisearchScore.
 */
export function searchExternalLinksMiniSearch(ms, query, limit = 6) {
  if (!ms || !query) return []
  const byId = new Map(EXTERNAL_LINKS.map((e) => [e.id, e]))
  return searchLocalRegistryMiniSearch(ms, query, undefined, limit).map((result) => {
    const entry = byId.get(result.id) || { id: result.id, label: result.label }
    return {
      ...entry,
      objectID: entry.id,
      __external: true,
      __localOrigin: true,
      __localEngine: 'minisearch',
      __minisearchScore: result.score,
      __minisearchTerms: result.matchedTerms,
    }
  })
}

// Single source of truth for the help-center deep link. Used by the dropdown's
// "See all results for help" row to push the user's query into the Plickers
// Help Center search.
const HELP_SEARCH_URL_BASE = 'https://help.plickers.com/hc/en-us/search'

export function buildHelpSearchUrl(query) {
  const trimmed = (query || '').trim()
  if (!trimmed) return HELP_SEARCH_URL_BASE
  return `${HELP_SEARCH_URL_BASE}?utf8=%E2%9C%93&query=${encodeURIComponent(trimmed)}`
}
